import React, { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import { endOfDay, startOfDay } from 'date-fns'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { CALENDAR_EVENT_VIDEO_PLATFORM_DISPLAY_NAME } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/appointmentModalityConstants'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  getProgramDisplayLabel,
  getProgramNameFromId,
} from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { SessionTypeText } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/messages'
import { isIntakeSession } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import { CalendarAppointmentsView } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { DEFAULT_VIDEO_PLATFORM, VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'
import { ProviderAgendaPanel } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/providerAgendaPanel/ProviderAgendaPanel'

import { getAgendaAppointments, isLateCancellation } from './agendaUtils'
import { actions as mixpanelActions, mixpanelEvents } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { RiskBadgeForClient } from '../../common/components/riskBadge/RiskBadgeWithPopover'
import { CLIENT_HOME } from '../../common/constants/routingConstants'
import { getClientFullName } from '../../common/utils/utils'
import { getAuthConfig, getAuthUserId } from '../../data/auth/authSelectors'
import { useGetAssignmentStatusesQuery } from '../../lyraTherapy/assignments/data/assignmentsApi'
import { useGetAppointmentsQuery } from '../../lyraTherapy/clients/clientDetails/data/appointmentsApi'
import { setSelectedClientId } from '../../lyraTherapy/clients/clientDetails/data/ltClientSlice'
import { useClientRiskFlags } from '../openItems/hooks/useClientRiskFlags'
import { useSharedItemHoverActions } from '../openItems/hooks/useSharedItemHoverActions'
import { useWithClient } from '../openItems/hooks/useWithClient'

type AgendaContentProps = {
  date: Date
}

const AgendaContent: React.FC<AgendaContentProps> = ({ date }) => {
  const userId = useSelector(getAuthUserId)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const intl = useIntl()
  const config = useSelector(getAuthConfig)
  const { isPreferredNameEnabled, isDAEngageOnOffPlatformVideoEnabled, enableDAEngageInfractionCollections } =
    useFlags()
  const showVideoPlatform = isDAEngageOnOffPlatformVideoEnabled && config?.supportsOnOffPlatformVideo
  const getSharedHoverActions = useSharedItemHoverActions('agenda')
  const atRiskClientIds = useClientRiskFlags()

  const { data: appointments, isLoading } = useGetAppointmentsQuery({
    providerId: userId,
    dateRange: { start: startOfDay(date).toISOString(), end: endOfDay(date).toISOString() },
    view: CalendarAppointmentsView.CALENDAR,
  })
  const { data: assignmentStatuses } = useGetAssignmentStatusesQuery({ providerId: userId })
  const hasCompletedOnboardingForms = useCallback(
    (clientId: string) =>
      !assignmentStatuses?.[clientId]?.some(
        (assignmentStatus) => assignmentStatus.status !== 'completed' && assignmentStatus.session_period === 0,
      ),
    [assignmentStatuses],
  )
  const { visibleAppointments, allCompleted } = useMemo(
    () => getAgendaAppointments(appointments, enableDAEngageInfractionCollections),
    [appointments, enableDAEngageInfractionCollections],
  )

  const { items: appointmentsWithClients, isLoading: isLoadingClients } = useWithClient(
    visibleAppointments,
    (appt) => appt.userId,
  )

  const mappedAppointments = useMemo(
    () =>
      appointmentsWithClients.map((appointment) => ({
        id: appointment.appointmentId,
        appointmentDate: getDateFromAppointment(appointment),
        isLateCancellation: isLateCancellation(appointment),
        clientName: getClientFullName(appointment.client, isPreferredNameEnabled),
        appointmentInfo: (
          <FormattedMessage
            defaultMessage='{program} · {sessionType} · {meetingFormat}'
            description='Appointment info line showing program, session type, and meeting modality'
            values={{
              program: getProgramDisplayLabel(intl, getProgramNameFromId(appointment.programId), [
                'daClientList',
                'short',
                'default',
              ]),
              sessionType: isIntakeSession(appointment)
                ? intl.formatMessage({ defaultMessage: 'Intake', description: 'First appointment session type' })
                : intl.formatMessage({
                    defaultMessage: 'Follow-up',
                    description: 'Subsequent appointment session type',
                  }),
              meetingFormat: (() => {
                const meetingFormatLabel =
                  SessionTypeText[appointment.meetingFormat as MEETING_FORMATS] ?? appointment.meetingFormat
                if (showVideoPlatform && appointment.meetingFormat === MEETING_FORMATS.VIDEO) {
                  return `${meetingFormatLabel} (${CALENDAR_EVENT_VIDEO_PLATFORM_DISPLAY_NAME[(appointment.videoPlatform as VIDEO_PLATFORM) ?? DEFAULT_VIDEO_PLATFORM]})`
                }
                return meetingFormatLabel
              })(),
            }}
          />
        ),
        completedOnboardingForms: appointment.sessionNumber > 1 || hasCompletedOnboardingForms(appointment.client.id),
        badges:
          !config?.hideRiskAlerts && atRiskClientIds.has(appointment.client.id) ? (
            <RiskBadgeForClient clientId={appointment.client.id} showRiskBadge logNoteSource='agenda' />
          ) : undefined,
        hoverActions: getSharedHoverActions(appointment.client),
        onPress: () => {
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action: mixpanelActions.AGENDA_ROW_PRESS,
            properties: {
              meeting_format: appointment.meetingFormat,
              session_type: isIntakeSession(appointment) ? 'intake' : 'follow_up',
            },
          })
          dispatch(setSelectedClientId(appointment.client.id))
          navigate(CLIENT_HOME.route)
        },
      })),
    [
      appointmentsWithClients,
      atRiskClientIds,
      config?.hideRiskAlerts,
      hasCompletedOnboardingForms,
      intl,
      isPreferredNameEnabled,
      showVideoPlatform,
      getSharedHoverActions,
      dispatch,
      navigate,
    ],
  )

  return (
    <ProviderAgendaPanel
      appointments={mappedAppointments}
      isLoading={isLoading || isLoadingClients}
      allCompleted={allCompleted}
    />
  )
}

export default AgendaContent
